// =========================================================================
// HelpyDoc · Tema claro/oscuro.
//
// Precedencia: elección explícita del usuario (localStorage) → preferencia
// del sistema → claro. El tema se materializa en `data-theme` del <html>,
// que es lo que leen las sobrescrituras de tokens (modern.css para la capa
// actual; app.css conserva las del piloto).
//
// Histórico: entre 2026-06 y 2026-07-25 estas funciones devolvían 'light' a
// fuego porque la capa moderna no tenía paleta oscura y el resultado era una
// interfaz a medias. Ahora modern.css define los tokens dark, así que el
// interruptor de Ajustes vuelve a ser real.
// =========================================================================
export type ThemeMode = 'light' | 'dark';

const LS_KEY = 'theme';

export function getSystemTheme(): ThemeMode {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function getStoredTheme(): ThemeMode | null {
  try {
    const v = localStorage.getItem(LS_KEY);
    return v === 'dark' || v === 'light' ? v : null;
  } catch {
    return null;
  }
}

export function resolveTheme(): ThemeMode {
  return getStoredTheme() ?? getSystemTheme();
}

export function applyTheme(theme: ThemeMode = resolveTheme()): void {
  document.documentElement.setAttribute('data-theme', theme);
  try {
    localStorage.setItem(LS_KEY, theme);
    // Clave heredada del piloto: se mantiene sincronizada por si algún
    // fragmento antiguo la sigue leyendo.
    localStorage.setItem('helpydoc_dark', theme === 'dark' ? '1' : '0');
  } catch {
    /* almacenamiento no disponible (modo privado): el tema sigue aplicado en el DOM */
  }
}

export function initTheme(): ThemeMode {
  const theme = resolveTheme();
  applyTheme(theme);
  return theme;
}

export function toggleTheme(current: ThemeMode = resolveTheme()): ThemeMode {
  const next: ThemeMode = current === 'dark' ? 'light' : 'dark';
  applyTheme(next);
  return next;
}
